import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, UserPlus, UserCheck, UserX, Search, Filter, Eye, Edit, 
  Trash2, Shield, Clock, CheckCircle, AlertCircle, XCircle,
  RefreshCw, Mail, Phone, Building2, Calendar, Key, Settings,
  ChevronRight, FileText, Car, Database, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('autogutachter24_token');

const AdminBenutzer = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const pendingRes = await fetch(`${API_URL}/api/admin/users/pending`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingUsers(data.users || []);
      }

      const usersRes = await fetch(`${API_URL}/api/admin/users/all?limit=100`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }

      const statsRes = await fetch(`${API_URL}/api/admin/users/stats/overview`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId, permissions = null) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: permissions ? JSON.stringify({ permissions }) : '{}'
      });
      if (res.ok) {
        toast.success('Benutzer freigeschaltet');
        loadData();
        setShowDetailModal(false);
      } else {
        toast.error('Fehler beim Freischalten');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const blockUser = async (userId) => {
    if (!window.confirm('Benutzer wirklich sperren?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/block`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success('Benutzer gesperrt');
        loadData();
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const unblockUser = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/unblock`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success('Benutzer entsperrt');
        loadData();
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success('Benutzer gelöscht');
        loadData();
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const viewUserDetail = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedUser(data);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const updateUserPermissions = async (userId, permissions) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ permissions })
      });
      if (res.ok) {
        toast.success('Berechtigungen aktualisiert');
        viewUserDetail(userId);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Wartend' },
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Aktiv' },
      blocked: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Gesperrt' },
      deleted: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Trash2, label: 'Gelöscht' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getRoleBadge = (rolle) => {
    const badges = {
      superadmin: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Superadmin' },
      admin: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Admin' },
      backoffice: { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'Backoffice' },
      gutachter: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Gutachter' }
    };
    const badge = badges[rolle] || badges.gutachter;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.vorname?.toLowerCase().includes(query) ||
      user.nachname?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.firma?.toLowerCase().includes(query)
    );
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              Benutzerverwaltung
            </h1>
            <p className="text-gray-500 mt-1">Benutzer verwalten, freischalten und Berechtigungen vergeben</p>
          </div>
          <Button onClick={loadData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-yellow-200 rounded-full">
                  <Clock className="w-6 h-6 text-yellow-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
                  <p className="text-sm text-yellow-600">Wartend</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-green-200 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-800">{stats.active}</p>
                  <p className="text-sm text-green-600">Aktiv</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-red-200 rounded-full">
                  <XCircle className="w-6 h-6 text-red-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-800">{stats.blocked}</p>
                  <p className="text-sm text-red-600">Gesperrt</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 bg-blue-200 rounded-full">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
                  <p className="text-sm text-blue-600">Gesamt</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:shadow px-6 gap-2">
              <Clock className="w-4 h-4" />
              Wartende Freischaltungen
              {pendingUsers.length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">{pendingUsers.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="data-[state=active]:bg-white data-[state=active]:shadow px-6 gap-2">
              <Users className="w-4 h-4" />
              Alle Benutzer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            {pendingUsers.length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-12 text-center">
                  <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600">Keine wartenden Registrierungen</h3>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map(user => (
                  <Card key={user.user_id} className="bg-white border-l-4 border-l-yellow-400 hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-700 font-bold text-lg">
                            {user.vorname?.[0]}{user.nachname?.[0]}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800 text-lg">{user.vorname} {user.nachname}</h3>
                            <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                              <Mail className="w-3 h-3" /> {user.email}
                            </p>
                            {user.telefon && (
                              <p className="text-gray-500 text-sm flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {user.telefon}
                              </p>
                            )}
                            {user.firma && (
                              <p className="text-gray-500 text-sm flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {user.firma}
                              </p>
                            )}
                            <p className="text-gray-400 text-xs mt-2 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Registriert am {new Date(user.created_at).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => viewUserDetail(user.user_id)} className="gap-1">
                            <Eye className="w-4 h-4" />
                            Details
                          </Button>
                          <Button onClick={() => approveUser(user.user_id)} size="sm" className="bg-green-600 hover:bg-green-700 gap-1">
                            <UserCheck className="w-4 h-4" />
                            Freischalten
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteUser(user.user_id)} className="text-red-600 border-red-300 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-6">
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle>Alle Benutzer ({filteredUsers.length})</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Suchen..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-gray-600">Benutzer</th>
                      <th className="text-left p-4 font-medium text-gray-600">Status</th>
                      <th className="text-left p-4 font-medium text-gray-600">Rolle</th>
                      <th className="text-left p-4 font-medium text-gray-600">Akten</th>
                      <th className="text-left p-4 font-medium text-gray-600">Zugänge</th>
                      <th className="text-right p-4 font-medium text-gray-600">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers.map(user => (
                      <tr key={user.user_id} className="hover:bg-gray-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium">
                              {user.vorname?.[0]}{user.nachname?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{user.vorname} {user.nachname}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">{getStatusBadge(user.status)}</td>
                        <td className="p-4">{getRoleBadge(user.rolle)}</td>
                        <td className="p-4"><span className="text-gray-600">{user.akten_count || 0}</span></td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            {user.permissions?.dat_access && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">DAT</span>}
                            {user.permissions?.gdv_access && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">GDV</span>}
                            {user.permissions?.restwert_access && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">RW</span>}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => viewUserDetail(user.user_id)}><Eye className="w-4 h-4" /></Button>
                            {user.status === 'active' && <Button variant="ghost" size="sm" onClick={() => blockUser(user.user_id)} className="text-red-600"><UserX className="w-4 h-4" /></Button>}
                            {user.status === 'blocked' && <Button variant="ghost" size="sm" onClick={() => unblockUser(user.user_id)} className="text-green-600"><UserCheck className="w-4 h-4" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {showDetailModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-lg">
                    {selectedUser.vorname?.[0]}{selectedUser.nachname?.[0]}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{selectedUser.vorname} {selectedUser.nachname}</h2>
                    <p className="text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
                <Button variant="ghost" onClick={() => setShowDetailModal(false)}>✕</Button>
              </div>

              <div className="p-6 space-y-6">
                <div className="flex items-center gap-4">
                  {getStatusBadge(selectedUser.status)}
                  {getRoleBadge(selectedUser.rolle)}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2"><Phone className="w-4 h-4" /> Telefon</h4>
                    <p className="text-gray-800">{selectedUser.telefon || '-'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2"><Building2 className="w-4 h-4" /> Firma</h4>
                    <p className="text-gray-800">{selectedUser.firma || '-'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg col-span-2">
                    <h4 className="font-medium text-gray-700 mb-2">Adresse</h4>
                    <p className="text-gray-800">{selectedUser.strasse || '-'}<br />{selectedUser.plz} {selectedUser.ort}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">Steuernummer</h4>
                    <p className="text-gray-800 font-mono">{selectedUser.steuernummer || '-'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">USt-ID</h4>
                    <p className="text-gray-800 font-mono">{selectedUser.ust_id || '-'}</p>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Aktivität</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-700">{selectedUser.akten_count || 0}</p>
                      <p className="text-sm text-blue-600">Akten</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('de-DE') : '-'}</p>
                      <p className="text-xs text-blue-600">Registriert</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleDateString('de-DE') : 'Nie'}</p>
                      <p className="text-xs text-blue-600">Letzter Login</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2"><Key className="w-4 h-4" /> API-Zugänge eingerichtet</h4>
                  <div className="flex gap-4">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedUser.has_dat_credentials ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                      <Car className="w-4 h-4" /> DAT {selectedUser.has_dat_credentials ? '✓' : '✕'}
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedUser.has_gdv_credentials ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                      <Globe className="w-4 h-4" /> GDV {selectedUser.has_gdv_credentials ? '✓' : '✕'}
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedUser.has_casion_credentials ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                      <Database className="w-4 h-4" /> Restwert {selectedUser.has_casion_credentials ? '✓' : '✕'}
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Berechtigungen</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'dat_access', label: 'DAT myClaim Zugang', icon: Car },
                      { key: 'gdv_access', label: 'GDV Zugang', icon: Globe },
                      { key: 'restwert_access', label: 'Restwertbörse', icon: Database },
                      { key: 'kalkulation_selbst', label: 'Selbst kalkulieren', icon: Settings },
                      { key: 'pdf_export', label: 'PDF Export', icon: FileText },
                      { key: 'email_versand', label: 'E-Mail Versand', icon: Mail },
                    ].map(perm => {
                      const Icon = perm.icon;
                      const isEnabled = selectedUser.permissions?.[perm.key];
                      return (
                        <label key={perm.key} className="flex items-center gap-3 p-2 rounded hover:bg-purple-100 cursor-pointer">
                          <input type="checkbox" checked={isEnabled || false} onChange={(e) => {
                            const newPerms = { ...selectedUser.permissions, [perm.key]: e.target.checked };
                            setSelectedUser(prev => ({ ...prev, permissions: newPerms }));
                          }} className="w-4 h-4 text-purple-600 rounded" />
                          <Icon className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700">{perm.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t border-purple-200">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      Max. Akten:
                      <Input type="number" value={selectedUser.permissions?.max_akten || 10} onChange={(e) => {
                        const newPerms = { ...selectedUser.permissions, max_akten: parseInt(e.target.value) };
                        setSelectedUser(prev => ({ ...prev, permissions: newPerms }));
                      }} className="w-20 h-8" />
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  {selectedUser.status === 'pending' && (
                    <Button onClick={() => approveUser(selectedUser.user_id, selectedUser.permissions)} className="bg-green-600 hover:bg-green-700 flex-1 gap-2">
                      <UserCheck className="w-4 h-4" /> Mit diesen Berechtigungen freischalten
                    </Button>
                  )}
                  {selectedUser.status === 'active' && (
                    <>
                      <Button onClick={() => updateUserPermissions(selectedUser.user_id, selectedUser.permissions)} className="bg-blue-600 hover:bg-blue-700 flex-1 gap-2">
                        <Settings className="w-4 h-4" /> Berechtigungen speichern
                      </Button>
                      <Button variant="outline" onClick={() => blockUser(selectedUser.user_id)} className="text-red-600 border-red-300 hover:bg-red-50 gap-2">
                        <UserX className="w-4 h-4" /> Sperren
                      </Button>
                    </>
                  )}
                  {selectedUser.status === 'blocked' && (
                    <Button onClick={() => unblockUser(selectedUser.user_id)} className="bg-green-600 hover:bg-green-700 flex-1 gap-2">
                      <UserCheck className="w-4 h-4" /> Entsperren
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBenutzer;
